import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NgApexchartsModule } from "ng-apexcharts";
import { EmocionesDiarioService } from "src/app/core/services/emociones.service";
import { DiarioEmocion, EmotionOption } from "src/app/core/models";

@Component({
  selector: "app-emociones",
  standalone: true,
  imports: [CommonModule, FormsModule, NgApexchartsModule],
  templateUrl: "./emociones.component.html",
  styleUrl: "./emociones.component.scss",
})
export class EmocionesComponent implements OnInit {
  currentUser: any;
  isLoading = true;

  todayDisplay = "";

  weekDays: {
    label: string;
    dayNumber: number;
    fecha: string;
    emocion: DiarioEmocion | null;
    isToday: boolean;
  }[] = [];

  showRegistro = false;
  selectedEmocion: string | null = null;
  anotacion = "";
  isSaving = false;
  todayEntry: DiarioEmocion | null = null;
  selectedDayEntry: DiarioEmocion | null = null;

  // Estadísticas del mes
  mesActualLabel = "";
  totalRegistrosMes = 0;
  rachaActual = 0;
  emocionFrecuente: EmotionOption | null = null;
  resumenEmociones: { emocion: EmotionOption; cantidad: number }[] = [];

  // Chart donut
  chartSeries: number[] = [];
  chartLabels: string[] = [];
  chartColors: string[] = [];
  chartOptions: any = {};
  showChart = false;

  private coloresEmociones: Record<string, string> = {
    alegria: "#FFD93D",
    ansiedad: "#FF6B6B",
    apatia: "#A8A8A8",
    entusiasmo: "#4ECDC4",
    frustracion: "#E85D75",
    gratitud: "#6BCB77",
    ilusion: "#9B59B6",
    ira: "#C0392B",
    miedo: "#5DADE2",
    motivacion: "#F39C12",
    tranquilidad: "#48C9B0",
    tristeza: "#5B7DB1",
  };

  emociones: EmotionOption[] = [
    { key: "alegria", label: "Alegría", icon: "assets/images/emociones/alegria.png" },
    { key: "ansiedad", label: "Ansiedad", icon: "assets/images/emociones/ansiedad.png" },
    { key: "apatia", label: "Apatía", icon: "assets/images/emociones/apatia.png" },
    { key: "entusiasmo", label: "Entusiasmo", icon: "assets/images/emociones/entusiamo.png" },
    { key: "frustracion", label: "Frustración", icon: "assets/images/emociones/frustracion.png" },
    { key: "gratitud", label: "Gratitud", icon: "assets/images/emociones/gratitud.png" },
    { key: "ilusion", label: "Ilusión", icon: "assets/images/emociones/ilusion.png" },
    { key: "ira", label: "Ira", icon: "assets/images/emociones/ira.png" },
    { key: "miedo", label: "Miedo", icon: "assets/images/emociones/miedo.png" },
    { key: "motivacion", label: "Motivación", icon: "assets/images/emociones/motivacion.png" },
    { key: "tranquilidad", label: "Tranquilidad", icon: "assets/images/emociones/tranquilidad.png" },
    { key: "tristeza", label: "Tristeza", icon: "assets/images/emociones/tristeza.png" },
  ];

  constructor(private __emocionesService: EmocionesDiarioService) {}

  async ngOnInit(): Promise<void> {
    const stored = localStorage.getItem("currentPatient");
    if (stored) {
      this.currentUser = JSON.parse(stored);
      this.buildTodayDisplay();
      this.buildWeekDays();
      await this.loadWeekData();
      await this.loadEstadisticasMes();
    }
    this.isLoading = false;
  }

  private buildTodayDisplay(): void {
    const now = new Date();
    const dias = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
    const meses = [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
    ];
    this.todayDisplay = `Hoy, ${dias[now.getDay()]} ${now.getDate()} de ${meses[now.getMonth()]}`;
  }

  private buildWeekDays(): void {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(now);
    monday.setDate(now.getDate() + mondayOffset);

    const labels = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
    this.weekDays = [];

    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const fecha = this.formatDate(d);
      this.weekDays.push({
        label: labels[i],
        dayNumber: d.getDate(),
        fecha,
        emocion: null,
        isToday: fecha === this.formatDate(now),
      });
    }
  }

  private async loadWeekData(): Promise<void> {
    const fechaInicio = this.weekDays[0].fecha;
    const fechaFin = this.weekDays[6].fecha;
    try {
      const entries = await this.__emocionesService.getByRangoFechas(
        this.currentUser.userId,
        fechaInicio,
        fechaFin
      );

      for (const entry of entries) {
        const day = this.weekDays.find((d) => d.fecha === entry.fecha);
        if (day) {
          day.emocion = entry;
        }
      }

      const today = this.weekDays.find((d) => d.isToday);
      this.todayEntry = today?.emocion || null;
    } catch (error) {
      console.error("Error cargando emociones de la semana:", error);
    }
  }

  private async loadEstadisticasMes(): Promise<void> {
    const now = new Date();
    const anio = now.getFullYear();
    const mes = now.getMonth() + 1;
    const meses = [
      "", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
    ];
    this.mesActualLabel = `${meses[mes]} ${anio}`;

    try {
      const entries = await this.__emocionesService.getByMes(
        this.currentUser.userId, anio, mes
      );

      this.totalRegistrosMes = entries.length;
      if (entries.length === 0) return;

      // Contar emociones
      const conteo: Record<string, number> = {};
      for (const e of entries) {
        conteo[e.emocion] = (conteo[e.emocion] || 0) + 1;
      }

      // Resumen ordenado por cantidad
      this.resumenEmociones = Object.entries(conteo)
        .map(([key, cantidad]) => ({
          emocion: this.emociones.find((e) => e.key === key)!,
          cantidad,
        }))
        .filter((r) => r.emocion)
        .sort((a, b) => b.cantidad - a.cantidad);

      this.emocionFrecuente = this.resumenEmociones[0]?.emocion || null;

      // Racha de días consecutivos (hasta hoy)
      const todayStr = this.formatDate(now);
      const fechasSet = new Set(entries.map((e) => e.fecha));
      let racha = 0;
      const d = new Date(now);
      while (fechasSet.has(this.formatDate(d))) {
        racha++;
        d.setDate(d.getDate() - 1);
      }
      this.rachaActual = racha;

      // Chart donut
      this.chartSeries = this.resumenEmociones.map((r) => r.cantidad);
      this.chartLabels = this.resumenEmociones.map((r) => r.emocion.label);
      this.chartColors = this.resumenEmociones.map(
        (r) => this.coloresEmociones[r.emocion.key] || "#999"
      );
      this.chartOptions = {
        chart: { type: "donut", height: 280 },
        legend: { show: false },
        dataLabels: { enabled: false },
        plotOptions: {
          pie: {
            donut: {
              size: "70%",
              labels: {
                show: true,
                name: { show: true, fontSize: "14px" },
                value: { show: true, fontSize: "20px", fontWeight: 700 },
                total: {
                  show: true,
                  label: "Total",
                  fontSize: "13px",
                  fontWeight: 600,
                },
              },
            },
          },
        },
        stroke: { width: 2, colors: ["#fff"] },
        tooltip: {
          y: { formatter: (val: number) => val + (val === 1 ? " día" : " días") },
        },
        responsive: [
          { breakpoint: 576, options: { chart: { height: 240 } } },
        ],
      };
      this.showChart = true;
    } catch (error) {
      console.error("Error cargando estadísticas del mes:", error);
    }
  }

  getEmotionIcon(key: string): string {
    return this.emociones.find((e) => e.key === key)?.icon || "";
  }

  getEmotionLabel(key: string): string {
    return this.emociones.find((e) => e.key === key)?.label || key;
  }

  verDia(day: { emocion: DiarioEmocion | null }): void {
    if (day.emocion) {
      this.selectedDayEntry = day.emocion;
    }
  }

  cerrarDetalleDia(): void {
    this.selectedDayEntry = null;
  }

  iniciarRegistro(): void {
    this.showRegistro = true;
    this.selectedEmocion = null;
    this.anotacion = "";
  }

  selectEmocion(key: string): void {
    this.selectedEmocion = key;
  }

  async crearRegistro(): Promise<void> {
    if (!this.selectedEmocion || this.isSaving) return;

    this.isSaving = true;
    try {
      const entry = await this.__emocionesService.crear({
        idPaciente: this.currentUser.userId,
        emocion: this.selectedEmocion,
        anotacion: this.anotacion.trim() || "",
        fecha: this.formatDate(new Date()),
      });

      this.todayEntry = entry;
      const today = this.weekDays.find((d) => d.isToday);
      if (today) today.emocion = entry;
      this.showRegistro = false;
    } catch (error) {
      console.error("Error creando registro de emoción:", error);
    } finally {
      this.isSaving = false;
    }
  }

  cancelarRegistro(): void {
    this.showRegistro = false;
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
}
